// FLAGS: personal-data
// The app's only redaction helper: strips emails, +-prefixed phone numbers, JWTs, `token=` query
// values and Bearer headers from a string before it is logged to Application Insights.
/**
 * PII (Personally Identifiable Information) redaction utility.
 * Used to sanitize URLs and error messages before logging to Application Insights.
 */

interface PiiPattern {
  pattern: RegExp;
  replacement: string;
}

// Phone number pattern for international formats (requires + prefix to avoid false positives)
const PHONE_PATTERN =
  /\(?\+\d{1,4}\)?[-.\s]?\(?\d{1,4}\)?[-.\s]?\d{2,4}[-.\s]?\d{2,4}[-.\s]?\d{2,4}/g;

const PII_PATTERNS: PiiPattern[] = [
  // Email addresses
  {
    pattern: /[\w.%+-]+@[\w.-]+\.[a-zA-Z]{2,}/g,
    replacement: "[EMAIL_REDACTED]",
  },
  // Phone numbers (international formats with required + prefix to avoid false positives)
  {
    pattern: PHONE_PATTERN,
    replacement: "[PHONE_REDACTED]",
  },
  // JWT tokens (three base64url-encoded segments separated by dots)
  {
    pattern: /eyJ[\w-]*\.eyJ[\w-]*\.[\w-]*/g,
    replacement: "[TOKEN_REDACTED]",
  },
  // Token query parameters (e.g., ?token=abc123)
  {
    pattern: /([?&]token=)[^&\s]*/gi,
    replacement: "$1[TOKEN_REDACTED]",
  },
  // Bearer tokens in authorization headers
  {
    pattern: /(Bearer\s+)\S+/gi,
    replacement: "$1[TOKEN_REDACTED]",
  },
];

// FLAGS: personal-data
// PII_PATTERNS is the whole of what is redacted. Anything else in a URL survives — notably the
// `searchTerm` query value on content search and the `error_description` an SSO provider returns —
// and both of the two callers pass a string that can contain one.
/**
 * Redacts PII from a string (URL, error message, etc.)
 * @param input - The string to redact PII from
 * @returns The string with PII redacted, or empty string if input is null/undefined
 */
export const redactPii = (input: string | undefined | null): string => {
  if (!input) return "";

  return PII_PATTERNS.reduce(
    (result, { pattern, replacement }) => result.replace(pattern, replacement),
    input,
  );
};

const emailDomainPattern = /@([\w.-]+)$/;

// FLAGS: personal-data
// Deliberately keeps the domain rather than redacting it, and AppInsights.ts attaches the result to
// every telemetry event — so all telemetry from a logged-in user names their employer.
export const getEmailDomain = (email?: string): string => {
  const match = emailDomainPattern.exec(email || "");
  return match ? match[1] : "unknown";
};
